import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import host from "../../../global.jsx";
import StepIndicator from "./StepIndicator";
import AIStep1_Configure from "./ai/AIStep1_Configure";
import AIStep2_Topics from "./ai/AIStep2_Topics";
import AIStep3_Generate from "./ai/AIStep3_Generate";
import AIStep4_Save from "./ai/AIStep4_Save";

const AI_STEPS = ["Configure", "Topics", "Review", "Save"];

/** Converts AI/legacy `{question, option1..4, answer}` items into the stored shape
 *  with option ids. Mirrors the backend mapper used for PDF import. */
const toQuestionShape = (generated) => {
  const letterIndex = { a: 0, b: 1, c: 2, d: 3 };
  return generated.map((q) => {
    const options = ["option1", "option2", "option3", "option4"]
      .filter((k) => q[k] && String(q[k]).trim())
      .map((k) => ({ id: `o_${crypto.randomUUID()}`, text: String(q[k]).trim() }));
    const answer = String(q.answer ?? "").trim();
    const byLetter = letterIndex[answer.toLowerCase()];
    const correct =
      byLetter !== undefined && byLetter < options.length
        ? options[byLetter]
        : options.find((o) => o.text === answer);
    return {
      id: `q_${crypto.randomUUID()}`,
      text: String(q.question ?? "").trim(),
      options,
      correct_option_id: correct?.id ?? null,
    };
  });
};

/**
 * The AI generation path, moved out of CreateMCQSet so the manual builder could
 * become a page of its own. Generation, topics and the save payload are unchanged.
 *
 * Only the two exits differ, because there is no longer a mode picker to return to:
 * a successful save and a back-press on step 1 both land on the set library, which
 * refetches on mount — so the old post-save state reset and fetchData() call are
 * both redundant here.
 */
const AIGenerateFlow = () => {
  const navigate = useNavigate();
  const accessToken = sessionStorage.getItem("accessToken");
  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  const [aiStep, setAiStep] = useState(1);
  const [jobRole, setJobRole] = useState("");
  const [minExp, setMinExp] = useState(0);
  const [maxExp, setMaxExp] = useState(1);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [isTopicsFetching, setIsTopicsFetching] = useState(false);
  const [topics, setTopics] = useState([]);
  const [questionsPerTopic, setQuestionsPerTopic] = useState({});
  const [questions, setQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [setName, setSetName] = useState("");
  const [mcqTimelimit, setMcqTimelimit] = useState(0);
  const [isSavingSet, setIsSavingSet] = useState(false);

  const handleFetchTopics = async () => {
    setIsTopicsFetching(true);
    try {
      const response = await axios.post(
        `${host}/suggest_topics`,
        { jobRole, minExp, maxExp },
        { headers: authHeaders }
      );
      const fetched = response.data.topics;
      if (Array.isArray(fetched)) {
        setSuggestedTopics(fetched);
        setAiStep(2);
      } else {
        toast.error("Received invalid topics format");
        setSuggestedTopics([]);
      }
    } catch {
      toast.error("Error fetching topics");
      setSuggestedTopics([]);
    } finally {
      setIsTopicsFetching(false);
    }
  };

  const handleAddTopic = (topic) => {
    if (!topics.includes(topic)) {
      setTopics((p) => [...p, topic]);
      setQuestionsPerTopic((p) => ({ ...p, [topic]: 0 }));
    }
  };

  const handleRemoveTopic = (topic) => {
    setTopics((p) => p.filter((t) => t !== topic));
    setQuestionsPerTopic((p) => { const u = { ...p }; delete u[topic]; return u; });
  };

  const handleCountChange = (topic, value) => {
    setQuestionsPerTopic((p) => ({ ...p, [topic]: Math.max(0, parseInt(value) || 0) }));
  };

  const handleFetchQuestions = async () => {
    setIsLoadingQuestions(true);
    setAiStep(3);
    try {
      const payload = {
        topic_counts: Object.entries(questionsPerTopic)
          .filter(([, count]) => count > 0)
          .map(([topic, count]) => ({ topic, count })),
        jobRole,
        minExp,
        maxExp,
      };
      const response = await axios.post(`${host}/fetchmcq`, payload, { headers: authHeaders });
      setQuestions(response.data);
    } catch {
      toast.error("Error fetching questions");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleDeleteGeneratedQuestion = (index) => {
    setQuestions((p) => p.filter((_, i) => i !== index));
  };

  // Generation itself is unchanged — only the save lands on the new schema.
  const handleSaveSet = async () => {
    if (!setName.trim()) { toast.error("Enter a set name"); return; }
    setIsSavingSet(true);
    try {
      const payload = {
        name: setName.trim(),
        description: "",
        questions: toQuestionShape(questions),
        config: {
          time_limit_minutes: mcqTimelimit > 0 ? mcqTimelimit : 30,
          pass_percent: 60,
          shuffle_questions: true,
          shuffle_options: true,
        },
      };
      const res = await axios.post(`${host}/mcq-sets`, payload, {
        headers: { ...authHeaders, "Content-Type": "application/json" },
      });
      if (res.data.status === "success") {
        toast.success("MCQ Set saved successfully!");
        navigate("/org-admin/mcqsets");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving set");
    } finally {
      setIsSavingSet(false);
    }
  };

  const goToStep = (n) => {
    if (n < aiStep) { setAiStep(n); return; }
  };

  const handleBack = () => {
    if (aiStep > 1) { setAiStep((s) => s - 1); return; }
    navigate("/org-admin/mcqsets");
  };

  return (
      <div className="min-h-screen">
        <div className="bg-bgPrimary flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 pb-16">
          {/* Page header */}
          <div className="flex items-center gap-2 px-6 pt-2 mb-2">
            <button
              onClick={handleBack}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
            </button>
            <span className="dark:text-gray-400 text-textSecondary">
              Generate MCQ Set with AI
            </span>
          </div>

          {/* Content */}
          <div className="w-full flex-1 mt-4">
            <div className="space-y-6">
              <StepIndicator
                steps={AI_STEPS}
                currentStep={aiStep}
                onStepClick={goToStep}
              />

              {aiStep === 1 && (
                <div className="px-12">
                  <AIStep1_Configure
                    jobRole={jobRole} setJobRole={setJobRole}
                    minExp={minExp} setMinExp={setMinExp}
                    maxExp={maxExp} setMaxExp={setMaxExp}
                    onNext={handleFetchTopics}
                    isLoading={isTopicsFetching}
                  />
                </div>
              )}

              {aiStep === 2 && (
                <div className="px-12">
                  <AIStep2_Topics
                    suggestedTopics={suggestedTopics}
                    topics={topics}
                    questionsPerTopic={questionsPerTopic}
                    onAddTopic={handleAddTopic}
                    onRemoveTopic={handleRemoveTopic}
                    onCountChange={handleCountChange}
                    onGenerate={handleFetchQuestions}
                    isGenerating={isLoadingQuestions}
                  />
                </div>
              )}

              {aiStep === 3 && (
                <div className="px-12">
                  <AIStep3_Generate
                    questions={questions}
                    isLoading={isLoadingQuestions}
                    onDeleteQuestion={handleDeleteGeneratedQuestion}
                    onNext={() => setAiStep(4)}
                  />
                </div>
              )}

              {aiStep === 4 && (
                <div className="px-12">
                  <AIStep4_Save
                    jobRole={jobRole}
                    minExp={minExp}
                    maxExp={maxExp}
                    topics={topics}
                    questionsPerTopic={questionsPerTopic}
                    questions={questions}
                    setName={setName}
                    setSetName={setSetName}
                    mcqTimelimit={mcqTimelimit}
                    setMcqTimelimit={setMcqTimelimit}
                    onSave={handleSaveSet}
                    onBack={() => setAiStep(3)}
                    isSaving={isSavingSet}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default AIGenerateFlow;
